import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { REACT_APP_API_URL } from "../../../../env";
import CircularProgressBar from "../../../../Common/ProgressBar/CircularProgressBar";

const defaultCategory = {
  categoryKey: "advertorial",
  title: "Advertorial",
  description: "",
  showOnWebsite: 1,
  bitStatus: 1,
  mediaFileCount: 0,
  thumbnailPath: null,
};

const MediaUploadDocuments = () => {
  const [documentCategory, setDocumentCategory] = useState("advertorial");
  const [description, setDescription] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("advertorial");
  const [categoryTitle, setCategoryTitle] = useState("Advertorial");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryShowOnWebsite, setCategoryShowOnWebsite] = useState(1);
  const [categoryStatus, setCategoryStatus] = useState(1);
  const [categoryThumbnail, setCategoryThumbnail] = useState(null);
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  const [mediaItems, setMediaItems] = useState([]);
  const [editMediaId, setEditMediaId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("advertorial");
  const [editStatus, setEditStatus] = useState(1);
  const [editWebsiteVisibility, setEditWebsiteVisibility] = useState(1);
  const [editFile, setEditFile] = useState(null);
  const [editThumbnail, setEditThumbnail] = useState(null);
  const [isSavingMediaEdit, setIsSavingMediaEdit] = useState(false);
  const [openCategories, setOpenCategories] = useState({});

  const isExplicitlyDisabled = (value) => {
    if (value === undefined || value === null || value === "") return false;
    if (typeof value === "boolean") return value === false;
    const normalized = String(value).trim().toLowerCase();
    return normalized === "0" || normalized === "false" || normalized === "off" || normalized === "no";
  };

  const formatMonthYear = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  };

  const titleize = (value) =>
    String(value || "others")
      .split(/[-_]/g)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

  const categoryOptions = useMemo(() => {
    const unique = new Map();
    categories.forEach((category) => {
      unique.set(String(category.categoryKey).toLowerCase(), category);
    });
    if (!unique.has("advertorial")) unique.set("advertorial", defaultCategory);
    if (!unique.has("newsletter"))
      unique.set("newsletter", { ...defaultCategory, categoryKey: "newsletter", title: "Newsletter" });
    if (!unique.has("campaign"))
      unique.set("campaign", { ...defaultCategory, categoryKey: "campaign", title: "Campaign" });
    if (!unique.has("booklet"))
      unique.set("booklet", { ...defaultCategory, categoryKey: "booklet", title: "Booklet" });
    if (!unique.has("brochure"))
      unique.set("brochure", { ...defaultCategory, categoryKey: "brochure", title: "Brochure" });
    if (!unique.has("article"))
      unique.set("article", { ...defaultCategory, categoryKey: "article", title: "Article" });
    if (!unique.has("others"))
      unique.set("others", { ...defaultCategory, categoryKey: "others", title: "Others" });
    return Array.from(unique.values());
  }, [categories]);

  const groupedMediaItems = useMemo(() => {
    const grouped = new Map();
    mediaItems.forEach((item) => {
      const key = String(item.vchDocumentCategory || "others").toLowerCase();
      const title = item.vchCategoryTitle || titleize(key);
      if (!grouped.has(key)) {
        grouped.set(key, { key, title, items: [] });
      }
      grouped.get(key).items.push(item);
    });

    const orderMap = new Map(
      categoryOptions.map((category, index) => [String(category.categoryKey).toLowerCase(), index])
    );

    return Array.from(grouped.values()).sort((a, b) => {
      const ai = orderMap.has(a.key) ? orderMap.get(a.key) : Number.MAX_SAFE_INTEGER;
      const bi = orderMap.has(b.key) ? orderMap.get(b.key) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return a.title.localeCompare(b.title);
    });
  }, [mediaItems, categoryOptions]);

  const toggleCategory = (categoryKey) => {
    setOpenCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const loadData = async () => {
    try {
      const [categoryRes, mediaRes] = await Promise.all([
        axios.get(`${REACT_APP_API_URL}api/dashboard/admin/media-categories`),
        axios.get(`${REACT_APP_API_URL}api/dashboard/admin/list-all-media`),
      ]);

      const categoryData = categoryRes?.data?.data || [];
      const mediaData = mediaRes?.data?.data || [];

      setCategories(categoryData);
      setMediaItems(mediaData);
    } catch (error) {
      console.error("Error fetching media vault data:", error);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const found =
      categoryOptions.find(
        (item) => String(item.categoryKey).toLowerCase() === String(selectedCategory).toLowerCase()
      ) || defaultCategory;
    setCategoryTitle(found.title || "");
    setCategoryDescription(found.description || "");
    setCategoryShowOnWebsite(Number(found.showOnWebsite ?? 1));
    setCategoryStatus(Number(found.bitStatus ?? 1));
    setCategoryThumbnail(null);
  }, [selectedCategory, categoryOptions]);

  const resetUploadForm = () => {
    setDocumentCategory("advertorial");
    setDescription("");
    setMediaFile(null);
    setThumbnailFile(null);
    setUploadProgress(0);
  };

  const handleCreateMedia = async (e) => {
    e.preventDefault();

    if (!mediaFile) {
      alert("Please select a media file.");
      return;
    }

    const formData = new FormData();
    formData.append("DocumentCategory", documentCategory);
    formData.append("Description", description);
    formData.append("media", mediaFile);
    if (thumbnailFile) formData.append("thumbnail", thumbnailFile);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}api/dashboard/admin/media-upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || 1;
            const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
            setUploadProgress(percentCompleted);
          },
        }
      );
      alert(response?.data?.message || "Media uploaded successfully.");
      resetUploadForm();
      await loadData();
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setIsSavingCategory(true);

    try {
      const formData = new FormData();
      formData.append("DocumentCategory", selectedCategory);
      formData.append("title", categoryTitle);
      formData.append("description", categoryDescription);
      formData.append("showOnWebsite", categoryShowOnWebsite);
      formData.append("bitStatus", categoryStatus);
      if (categoryThumbnail) formData.append("thumbnail", categoryThumbnail);

      const response = await axios.put(
        `${REACT_APP_API_URL}api/dashboard/admin/media-categories/${selectedCategory}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      alert(response?.data?.message || "Category updated successfully.");
      await loadData();
    } catch (error) {
      console.error("Category update failed:", error);
      alert("Category update failed.");
    } finally {
      setIsSavingCategory(false);
    }
  };

  const startEditMedia = (item) => {
    setEditMediaId(item.intMediaId);
    setEditTitle(item.vchDescription || "");
    setEditCategory(item.vchDocumentCategory || "others");
    setEditStatus(isExplicitlyDisabled(item.bitStatus) ? 0 : 1);
    setEditWebsiteVisibility(isExplicitlyDisabled(item.bitItemShowOnWebsite) ? 0 : 1);
    setEditFile(null);
    setEditThumbnail(null);
  };

  const cancelEditMedia = () => {
    setEditMediaId(null);
    setEditTitle("");
    setEditCategory("others");
    setEditStatus(1);
    setEditWebsiteVisibility(1);
    setEditFile(null);
    setEditThumbnail(null);
  };

  const saveEditMedia = async (mediaId) => {
    setIsSavingMediaEdit(true);
    try {
      const formData = new FormData();
      formData.append("title", editTitle);
      formData.append("DocumentCategory", editCategory);
      formData.append("bitStatus", editStatus);
      formData.append("showOnWebsite", editWebsiteVisibility);
      if (editFile) formData.append("media", editFile);
      if (editThumbnail) formData.append("thumbnail", editThumbnail);

      const response = await axios.put(
        `${REACT_APP_API_URL}api/dashboard/admin/media-upload/${mediaId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      alert(response?.data?.message || "Media updated successfully.");
      cancelEditMedia();
      await loadData();
    } catch (error) {
      console.error("Media update failed:", error);
      alert("Media update failed.");
    } finally {
      setIsSavingMediaEdit(false);
    }
  };

  const toggleMediaWebsiteVisibility = async (item) => {
    try {
      const nextShowOnWebsite = isExplicitlyDisabled(item.bitItemShowOnWebsite) ? 1 : 0;
      const response = await axios.patch(
        `${REACT_APP_API_URL}api/dashboard/admin/media-upload/${item.intMediaId}/status`,
        { showOnWebsite: nextShowOnWebsite }
      );
      alert(response?.data?.message || "Media website visibility updated.");
      await loadData();
    } catch (error) {
      console.error("Media website visibility update failed:", error);
      alert("Media website visibility update failed.");
    }
  };

  const deleteMedia = async (item) => {
    if (!window.confirm("Are you sure you want to delete this media item?")) return;
    try {
      const response = await axios.delete(
        `${REACT_APP_API_URL}api/dashboard/admin/media-upload/${item.intMediaId}`
      );
      alert(response?.data?.message || "Media deleted successfully.");
      await loadData();
    } catch (error) {
      console.error("Media delete failed:", error);
      alert("Media delete failed.");
    }
  };

  return (
    <div className="container mt-4">
      <style>
        {`
          .mv-accordion-item {
            border: 1px solid #e9ecef;
            border-radius: 10px;
            margin-bottom: 12px;
            overflow: hidden;
            background: #fff;
          }
          .mv-accordion-header {
            width: 100%;
            border: 0;
            background: #f8f9fa;
            padding: 12px 14px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            transition: background-color 0.2s ease;
          }
          .mv-accordion-header:hover {
            background: #f1f3f5;
          }
          .mv-chevron {
            transition: transform 0.25s ease;
            font-size: 14px;
            color: #6c757d;
            width: 16px;
            text-align: center;
          }
          .mv-chevron.open {
            transform: rotate(90deg);
          }
          .mv-accordion-body {
            display: grid;
            grid-template-rows: 0fr;
            transition: grid-template-rows 0.28s ease;
          }
          .mv-accordion-body.open {
            grid-template-rows: 1fr;
          }
          .mv-accordion-body-inner {
            overflow: hidden;
          }
          .mv-table th {
            background: #fafbfc;
            font-size: 12px;
            letter-spacing: 0.2px;
            padding: 10px 12px !important;
            vertical-align: middle;
          }
          .mv-table td {
            padding: 12px !important;
            vertical-align: middle;
          }
          .mv-row-tone-even {
            background: #ffffff;
          }
          .mv-row-tone-odd {
            background: #fcfcfd;
          }
          .mv-row-main:hover {
            background: #f7fbff !important;
          }
          .mv-preview-frame {
            width: 68px;
            height: 68px;
            border-radius: 8px;
            border: 1px solid #dee2e6;
            background: #f8f9fa;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .mv-preview-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .mv-preview-fallback {
            font-size: 11px;
            color: #868e96;
            text-align: center;
            line-height: 1.2;
          }
          .mv-status-wrap {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
          }
          .mv-pill {
            font-size: 11px;
            font-weight: 600;
            padding: 4px 8px;
            border-radius: 999px;
          }
          .mv-action-group {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
          }
        `}
      </style>

      <div className="card shadow mb-4" style={{ position: "relative" }}>
        <div className="card-header bg-primary text-white">
          <h3 className="mb-0">Upload Media (Media Vault)</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleCreateMedia}>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Document Category</label>
                <select
                  className="form-select"
                  value={documentCategory}
                  onChange={(e) => setDocumentCategory(e.target.value)}
                >
                  {categoryOptions.map((item) => (
                    <option key={item.categoryKey} value={item.categoryKey}>
                      {item.title || item.categoryKey}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Upload File</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="thumbnail" className="form-label">
                  Upload Thumbnail
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="thumbnail"
                  onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-8">
                <label className="form-label">Title/Description</label>
                <textarea
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={120}
                  placeholder="Max 120 characters"
                />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button type="submit" className="btn btn-success" disabled={isUploading}>
                {isUploading ? "Uploading..." : "Submit"}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={resetUploadForm}
                disabled={isUploading}
              >
                Reset
              </button>
            </div>
          </form>

          {isUploading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255,255,255,0.9)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
              }}
            >
              <CircularProgressBar progress={uploadProgress} />
            </div>
          )}
        </div>
      </div>

      <div className="card shadow mb-4">
        <div className="card-header bg-light">
          <h5 className="mb-0">Media Category Controls</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSaveCategory}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categoryOptions.map((item) => (
                    <option key={item.categoryKey} value={item.categoryKey}>
                      {item.title || item.categoryKey}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Category Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={categoryTitle}
                  onChange={(e) => setCategoryTitle(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Category Thumbnail/Banner</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) => setCategoryThumbnail(e.target.files?.[0] || null)}
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Category Description</label>
                <textarea
                  className="form-control"
                  value={categoryDescription}
                  onChange={(e) => setCategoryDescription(e.target.value)}
                  rows={2}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Show on Website</label>
                <select
                  className="form-select"
                  value={categoryShowOnWebsite}
                  onChange={(e) => setCategoryShowOnWebsite(Number(e.target.value))}
                >
                  <option value={1}>Yes</option>
                  <option value={0}>No</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Display Status</label>
                <select
                  className="form-select"
                  value={categoryStatus}
                  onChange={(e) => setCategoryStatus(Number(e.target.value))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Media Count</label>
                <input
                  className="form-control"
                  value={
                    categoryOptions.find((item) => item.categoryKey === selectedCategory)?.mediaFileCount ?? 0
                  }
                  disabled
                />
              </div>
            </div>
            <div className="d-flex justify-content-end mt-3">
              <button type="submit" className="btn btn-primary" disabled={isSavingCategory}>
                {isSavingCategory ? "Saving..." : "Save Category"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow">
        <div className="card-header bg-light">
          <h5 className="mb-0">Media Materials</h5>
        </div>
        <div className="card-body">
          {groupedMediaItems.length === 0 && (
            <div className="text-center py-4">No media found.</div>
          )}

          {groupedMediaItems.map((group) => {
            const isOpen = Boolean(openCategories[group.key]);
            return (
              <div key={group.key} className="mv-accordion-item">
                <button
                  type="button"
                  className="mv-accordion-header"
                  onClick={() => toggleCategory(group.key)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className={`mv-chevron ${isOpen ? "open" : ""}`}>{">"}</i>
                    <span className="fw-semibold">{group.title}</span>
                  </div>
                  <span className="badge bg-secondary-subtle text-dark border">
                    {group.items.length}
                  </span>
                </button>

                <div className={`mv-accordion-body ${isOpen ? "open" : ""}`}>
                  <div className="mv-accordion-body-inner">
                    <div className="table-responsive p-2">
                      <table className="table table-sm table-bordered align-middle mb-0 mv-table">
                        <thead>
                          <tr>
                            <th style={{ width: "90px" }}>Preview</th>
                            <th>Title</th>
                            <th style={{ width: "140px" }}>Category</th>
                            <th style={{ width: "220px" }}>Status</th>
                            <th style={{ width: "130px" }}>Uploaded</th>
                            <th style={{ width: "210px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map((item, index) => {
                            const isEditing = editMediaId === item.intMediaId;
                            const rowToneClass = index % 2 === 0 ? "mv-row-tone-even" : "mv-row-tone-odd";
                            const baseUrl = REACT_APP_API_URL.replace(/\/$/, "");

                            const thumbnailUrl = item.vchThumbnailPath
                              ? `${baseUrl}/${String(item.vchThumbnailPath)
                                .replace(/\\/g, "/")
                                .replace(/^\/+/, "")}`
                              : null;

                            const fileUrl = item.vchFilePath
                              ? `${baseUrl}/${String(item.vchFilePath)
                                .replace(/\\/g, "/")
                                .replace(/^\/+/, "")}`
                              : null;

                            return (
                              <React.Fragment key={item.intMediaId}>
                                <tr className={`mv-row-main ${rowToneClass}`}>
                                  <td>
                                    {thumbnailUrl ? (
                                      <div className="mv-preview-frame">
                                        <img
                                          src={thumbnailUrl}
                                          alt={item.vchDescription || "thumbnail"}
                                          className="mv-preview-img"
                                        />
                                      </div>
                                    ) : (
                                      <div className="mv-preview-frame">
                                        <div className="mv-preview-fallback">No Preview</div>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <div className="fw-semibold">{item.vchDescription || "Untitled"}</div>
                                    {fileUrl && (
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="small text-decoration-none"
                                      >
                                        View File
                                      </a>
                                    )}
                                  </td>
                                  <td>{item.vchCategoryTitle || item.vchDocumentCategory}</td>
                                  <td>
                                    <div className="mv-status-wrap">
                                      <span
                                        className={`mv-pill badge ${!isExplicitlyDisabled(item.bitStatus) ? "bg-success" : "bg-secondary"
                                          }`}
                                      >
                                        {!isExplicitlyDisabled(item.bitStatus) ? "Published" : "Hidden"}
                                      </span>
                                      <span
                                        className={`mv-pill badge ${!isExplicitlyDisabled(item.bitItemShowOnWebsite)
                                            ? "bg-info text-dark"
                                            : "bg-dark"
                                          }`}
                                      >
                                        {!isExplicitlyDisabled(item.bitItemShowOnWebsite) ? "Visible" : "Hidden"}
                                      </span>
                                    </div>
                                  </td>
                                  <td>{formatMonthYear(item.dtmCreatedOn)}</td>
                                  <td>
                                    <div className="mv-action-group">
                                      <button
                                        className="btn btn-sm btn-outline-primary py-0 px-2"
                                        onClick={() => startEditMedia(item)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-warning py-0 px-2"
                                        onClick={() => toggleMediaWebsiteVisibility(item)}
                                      >
                                        {!isExplicitlyDisabled(item.bitItemShowOnWebsite) ? "Hide" : "Show"}
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger py-0 px-2"
                                        onClick={() => deleteMedia(item)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>

                                {isEditing && (
                                  <tr className={rowToneClass}>
                                    <td colSpan={6}>
                                      <div className="p-2 bg-light rounded">
                                        <div className="row g-2">
                                          <div className="col-md-4">
                                            <label className="form-label small mb-1">Title</label>
                                            <input
                                              className="form-control form-control-sm"
                                              value={editTitle}
                                              onChange={(e) => setEditTitle(e.target.value)}
                                            />
                                          </div>
                                          <div className="col-md-2">
                                            <label className="form-label small mb-1">Category</label>
                                            <select
                                              className="form-select form-select-sm"
                                              value={editCategory}
                                              onChange={(e) => setEditCategory(e.target.value)}
                                            >
                                              {categoryOptions.map((option) => (
                                                <option key={option.categoryKey} value={option.categoryKey}>
                                                  {option.title || option.categoryKey}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          <div className="col-md-2">
                                            <label className="form-label small mb-1">Publish</label>
                                            <select
                                              className="form-select form-select-sm"
                                              value={editStatus}
                                              onChange={(e) => setEditStatus(Number(e.target.value))}
                                            >
                                              <option value={1}>Published</option>
                                              <option value={0}>Hidden</option>
                                            </select>
                                          </div>
                                          <div className="col-md-2">
                                            <label className="form-label small mb-1">Visibility</label>
                                            <select
                                              className="form-select form-select-sm"
                                              value={editWebsiteVisibility}
                                              onChange={(e) => setEditWebsiteVisibility(Number(e.target.value))}
                                            >
                                              <option value={1}>Visible</option>
                                              <option value={0}>Hidden</option>
                                            </select>
                                          </div>
                                          <div className="col-md-2">
                                            <label className="form-label small mb-1">Replace File</label>
                                            <input
                                              type="file"
                                              className="form-control form-control-sm"
                                              onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                                            />
                                          </div>
                                          <div className="col-md-4">
                                            <label className="form-label small mb-1">Replace Thumbnail</label>
                                            <input
                                              type="file"
                                              className="form-control form-control-sm"
                                              onChange={(e) => setEditThumbnail(e.target.files?.[0] || null)}
                                            />
                                          </div>
                                          <div className="col-md-8 d-flex align-items-end justify-content-end gap-2">
                                            <button
                                              className="btn btn-sm btn-success"
                                              onClick={() => saveEditMedia(item.intMediaId)}
                                              disabled={isSavingMediaEdit}
                                            >
                                              {isSavingMediaEdit ? "Saving..." : "Save"}
                                            </button>
                                            <button className="btn btn-sm btn-secondary" onClick={cancelEditMedia}>
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MediaUploadDocuments;
